import { GameStatus } from '@betterpool/api-types/games-service';
import type { ContestSettings } from '@betterpool/api-types/contests-service';

import { Text } from '~/domains/design-system/Text';
import type { PlayerPoolMember } from '~/domains/contest/domains/services/interfaces/player-pool-member.interface';
import type { PlayerDetail } from '~/domains/games/interfaces/player-detail.interface';
import { getAbbreviatedTierDisplayLabel } from '~/domains/contest/domains/common/utils/playerCardDisplay';
import getFormattedScore from '~/domains/contest/utils/getFormattedScore';
import type { PickForEntry } from '~/services/contests/interfaces/picks-for-entry.interface';
import { CurrencyConvertor } from '~/components/utils/formatters';

import type { PlayerInfo } from '../../types/player';
import formatEarnings from '../../utils/formatEarnings';
import { toFixedGolfStat } from '../golfStatFormatters';

import { formatNextGameDate, parseInjuryTag } from './utils';

const HOLES_PER_ROUND = 18;
const TOTAL_ROUNDS = 4;

const parseMeta = (pick: PickForEntry | PlayerDetail) => {
  const thru = pick?.playerActivities?.thru || 0;
  const currentRound =
    thru > HOLES_PER_ROUND * (TOTAL_ROUNDS - 1) ? TOTAL_ROUNDS : Math.ceil(thru / HOLES_PER_ROUND);
  let roundThru: string | number = thru === 0 ? 0 : thru - (currentRound - 1) * HOLES_PER_ROUND;
  roundThru = roundThru % 18 === 0 && roundThru !== 0 ? 'F' : roundThru;

  const rounds = pick?.playerActivities?.rounds;
  if (Array.isArray(rounds) && rounds.length) {
    const items = rounds
      .filter((round) => round.competing_status !== 'CUT')
      .map((round) => ({
        label: null,
        value: [
          `R${round.round}`,
          typeof round.thru === 'number' && round.thru < HOLES_PER_ROUND && `thru ${round.thru}`,
          typeof round.score === 'number' && `(${getFormattedScore(round.score)})`,
        ]
          .filter(Boolean)
          .join(''),
      }));

    if (rounds.some((round) => round.competing_status === 'CUT')) {
      items.push({
        label: null,
        value: 'Missed Cut',
      });
    }

    return items;
  }

  return [
    {
      label: 'Round',
      value: currentRound,
    },
    {
      label: 'Thru',
      value: roundThru,
    },
  ];
};

const parseNonScheduled = (
  pick: PickForEntry | PlayerDetail,
  scoreType?: ContestSettings['scoreType']
): Partial<PlayerInfo> => {
  const isLive = pick.attributes.gameStatus === GameStatus.IN_PROGRESS;
  const isUpcoming = pick.attributes.gameStatus === GameStatus.SCHEDULED;

  return {
    isLive,
    ...(typeof pick?.score === 'number' && {
      points: {
        value: getFormattedScore(pick.score, { scoreType }),
        label: '',
      },
    }),
    meta: parseMeta(pick),
    play: (() => {
      const isTied = pick?.playerActivities?.tied;

      return (
        <Text variant="body-small">
          POS{' '}
          <Text variant="subhead-x-small">
            {(() => {
              if (
                ['wd', 'cut'].includes(
                  (pick as PickForEntry)?.playerActivities?.competing_status?.toLowerCase()
                )
              ) {
                return (pick as PickForEntry)?.playerActivities?.competing_status;
              }

              return `${isTied ? 'T' : ''}${pick?.playerActivities?.position}`;
            })()}
          </Text>
          {scoreType === 'dollars'
            ? ` (${getFormattedScore(pick?.playerActivities?.score as number)})`
            : undefined}
        </Text>
      );
    })(),
    nextPlay: isUpcoming
      ? formatNextGameDate(
          new Date(
            (pick as PickForEntry).attributes.gameStartDateTime ?? pick.attributes.gameStartDate
          )
        )
      : undefined,
    tableViewData: {
      score: (() => {
        if (['golf_score', 'fantasy_points'].includes(scoreType)) {
          return getFormattedScore(pick?.score);
        }

        return getFormattedScore((pick as PickForEntry)?.playerActivities?.score);
      })(),
      earnings: (() => {
        if (scoreType === 'dollars') {
          return typeof pick?.score === 'number'
            ? CurrencyConvertor(pick?.score).replace('US', '')
            : '-';
        }

        return typeof pick?.playerActivities?.money === 'number'
          ? CurrencyConvertor((pick as PickForEntry)?.playerActivities?.money).replace('US', '')
          : '-';
      })(),
      position: ['wd', 'cut'].includes(
        (pick as PickForEntry)?.playerActivities?.competing_status?.toLowerCase()
      )
        ? ((pick as PickForEntry)?.playerActivities?.competing_status as string)
        : `${(pick as PickForEntry)?.playerActivities?.tied ? 'T' : ''}${
            (pick as PickForEntry)?.playerActivities?.position ?? '-'
          }`,
    },
  };
};

const parseScheduled = (
  pick: PlayerPoolMember,
  scoreType?: ContestSettings['scoreType']
): Partial<PlayerInfo> => ({
  nextPlay: formatNextGameDate(new Date(pick.attributes.gameStartDateTime)),
  meta: (() => {
    const bestTournamentFinish = pick?.attributes?.best_tournament_finish;
    const hasBestTournamentFinish = bestTournamentFinish && bestTournamentFinish !== '';
    const owgrRank = pick?.attributes?.owgr_rank;
    const hasOWGRRank = !!owgrRank;
    const rankMeta = {
      label: hasOWGRRank ? 'OWGR' : 'RANK',
      value: hasOWGRRank ? owgrRank : (pick?.attributes?.datagolf_rank ?? '-'),
    };
    const avgMeta = {
      label: 'AVG',
      value: toFixedGolfStat(pick?.attributes?.scoring_avg, 1),
    };
    if (scoreType === 'dollars') {
      return [
        rankMeta,
        {
          label: 'T25s',
          value: pick?.attributes?.top_25 ?? '-',
        },
        pick?.attributes?.earnings
          ? {
              label: 'EARNINGS',
              value:
                typeof pick?.attributes?.earnings === 'number'
                  ? (formatEarnings(pick?.attributes?.earnings) ?? '-')
                  : '-',
            }
          : avgMeta,
      ];
    }

    return [
      rankMeta,
      avgMeta,
      scoreType === 'golf_score' && hasBestTournamentFinish
        ? {
            label: 'PGAC BEST',
            value: bestTournamentFinish,
          }
        : {
            label: 'T10s',
            value: pick?.attributes?.top_10 ?? '-',
          },
    ];
  })(),
});

const parseBasePlayerInfo = (pick: PlayerPoolMember): Partial<PlayerInfo> => ({
  tag: {
    playerTier: pick.tierId ? getAbbreviatedTierDisplayLabel(pick.tierId) : undefined,
    injury: (() => {
      const rounds = pick?.playerActivities?.rounds;
      if (Array.isArray(rounds) && rounds.length) {
        if (rounds.some((r) => r.competingStatus === 'CUT')) {
          return 'CUT';
        }
      }

      return parseInjuryTag(pick);
    })(),
    country: pick.attributes.country,
    countryAbbr: pick.attributes.countryAbb,
  },
});

const parseGolfPlayerInfo = (
  pick: PlayerPoolMember,
  pickWithLiveData: PickForEntry,
  scoreType?: ContestSettings['scoreType']
): Partial<PlayerInfo> => {
  const hasStarted =
    (pick.attributes.gameStatus === GameStatus.IN_PROGRESS &&
      (pick.playerActivities?.rounds?.[0]?.strokes > 0 || pick.playerActivities?.strokes > 0)) ||
    [GameStatus.FINISHED, GameStatus.FINALIZED].includes(pick.attributes.gameStatus) ||
    !!pick.playerActivities?.competingStatus;

  return {
    ...(!hasStarted
      ? parseScheduled(pick, scoreType)
      : parseNonScheduled(pickWithLiveData || (pick as PickForEntry), scoreType)),
    ...parseBasePlayerInfo(pick),
    isUpcoming: !hasStarted,
  };
};

export default parseGolfPlayerInfo;

export { parseNonScheduled as parseGolfPlayerInfoAfterGameStart };
